import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { CATEGORIES, catalog } from "@/lib/catalog";

const FORM_URL = "https://readdy.ai/api/form/db7rev7idngsrf4ehol0";

export default function AboutPage() {
  useDocumentMeta(
    "关于我们 - 亚洲视频一区",
    "了解亚洲视频一区：一个专注免费高清影视在线播放的演示平台，涵盖电影、电视剧与综艺节目，并提供版权与免责声明及联系方式。",
    "亚洲视频一区,关于我们,影视平台,版权声明,联系方式"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      form.reset();
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        form.reset();
        setStatus("success");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      {/* Intro hero */}
      <section className="relative w-full">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-background-100 to-secondary-950/40" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-12 md:py-20">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 max-w-3xl">
            关于亚洲视频一区
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">
            亚洲视频一区是一个专注免费高清影视在线播放的内容平台，致力于为广泛的影视爱好者提供
            便捷的浏览、筛选与观看体验。我们汇聚电影、电视剧与综艺节目等丰富资源，
            用清晰的信息结构、友好的界面与流畅的交互，让每一次找片、看片都轻松愉快。
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 md:gap-6 max-w-2xl">
            {[
              { value: `${catalog.length}+`, label: "精选影视内容" },
              { value: `${CATEGORIES.length}`, label: "核心内容频道" },
              { value: "免费", label: "高清在线观看" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg bg-background-50/60 backdrop-blur border border-background-300/50 p-4"
              >
                <p className="font-heading text-2xl md:text-3xl font-extrabold text-primary-500">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs md:text-sm text-foreground-700">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Left column */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          <section className="rounded-lg bg-background-100 border border-background-300/50 p-5 md:p-7">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-flag-2-line text-primary-500" />
              我们的愿景
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              我们相信，好内容值得被更多人轻松发现。亚洲视频一区希望用最直观的方式，
              把丰富的影视资源呈现给每一位观众：清晰的分类、可靠的筛选、详尽的影片资料，
              以及简洁流畅的播放界面。无论你是想追一部热播长剧，还是随手点开一部高分电影，
              都能在几步之内找到并开始观看。
            </p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: "ri-film-line", text: "电影、电视剧、综艺多频道覆盖" },
                { icon: "ri-filter-3-line", text: "按地区、年份、类型精准筛选" },
                { icon: "ri-star-line", text: "评分与热度一目了然的推荐" },
                { icon: "ri-device-line", text: "电脑与手机端同样顺滑的体验" },
              ].map((item) => (
                <li
                  key={item.text}
                  className="flex items-center gap-3 rounded-md bg-background-200/50 px-4 py-3"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500/15 text-primary-500 shrink-0">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <span className="text-sm text-foreground-800">{item.text}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-lg bg-background-100 border border-background-300/50 p-5 md:p-7">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-500" />
              版权与免责声明
            </h2>
            <div className="mt-4 flex flex-col gap-3 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                本站为影视内容浏览与播放功能的<strong className="text-foreground-900">演示站点</strong>，
                页面上出现的所有影片名称、海报、简介、评分、播放数据等均为虚构示例，仅用于界面与交互效果展示。
              </p>
              <p>
                本站不提供、不存储也不传播任何真实的影视资源与视频流，播放区域为前端演示控件，
                不会产生真实的播放行为。若本站展示的结构或内容对任何版权方造成困扰，请通过下方联系方式与我们沟通。
              </p>
              <p className="rounded-md bg-secondary-500/10 border border-secondary-500/25 px-4 py-3 text-secondary-500 text-sm">
                温馨提示：请支持正版影视内容，遵守相关版权法律法规。
              </p>
            </div>
          </section>

          <section className="rounded-lg bg-background-100 border border-background-300/50 p-5 md:p-7">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-contacts-line text-primary-500" />
              联系方式
            </h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 rounded-md bg-background-200/50 px-4 py-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/15 text-primary-500 shrink-0">
                  <i className="ri-mail-line text-xl" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">合作与版权邮箱</p>
                  <a
                    href="mailto:support@yazhoushipin1.com"
                    className="text-sm text-foreground-900 hover:text-primary-500 transition-colors truncate block"
                  >
                    support@yazhoushipin1.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md bg-background-200/50 px-4 py-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/15 text-primary-500 shrink-0">
                  <i className="ri-customer-service-2-line text-xl" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">用户反馈</p>
                  <p className="text-sm text-foreground-900">欢迎使用右侧反馈表单</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Feedback form */}
        <div className="lg:col-span-1">
          <section
            id="feedback-form"
            className="rounded-lg bg-background-100 border border-background-300/50 p-5 md:p-6 lg:sticky lg:top-24"
          >
            <h2 className="font-heading text-xl font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-chat-3-line text-primary-500" />
              意见反馈
            </h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              对网站有任何建议或问题？填写下面的表单告诉我们，我们会认真阅读每一条反馈。
            </p>

            {status === "success" ? (
              <div className="mt-5 rounded-md bg-secondary-500/10 border border-secondary-500/30 px-4 py-6 text-center">
                <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-500 text-background-50 mb-3">
                  <i className="ri-check-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-900 font-medium">提交成功，感谢你的反馈！</p>
                <p className="mt-1 text-xs text-foreground-600">我们会尽快查看并处理。</p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-4 text-xs text-primary-500 hover:underline cursor-pointer"
                >
                  再提交一条
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
                <div>
                  <label className="block text-xs text-foreground-600 mb-1.5" htmlFor="fb-name">
                    称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full bg-background-200/60 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-foreground-600 mb-1.5" htmlFor="fb-email">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full bg-background-200/60 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-foreground-600 mb-1.5" htmlFor="fb-topic">
                    反馈类型
                  </label>
                  <select
                    id="fb-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full bg-background-200/60 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-500/70 transition-colors"
                  >
                    <option>内容建议</option>
                    <option>使用问题</option>
                    <option>版权相关</option>
                    <option>合作洽谈</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-foreground-600 mb-1.5" htmlFor="fb-message">
                    反馈内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述你的建议或问题（不超过 500 字）"
                    className="w-full bg-background-200/60 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 transition-colors resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-extra-note"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="text-xs text-primary-400 bg-primary-500/10 border border-primary-500/25 rounded-md px-3 py-2">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-send-plane-line" />
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>
                <p className="text-[11px] text-foreground-500 text-center">
                  我们仅将信息用于回复你的反馈，不会对外公开。
                </p>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}